import * as React from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  cn,
} from "@nexus/ui"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  connectionsQueryOptions,
  orderHandoffDefaultsQueryOptions,
  useCreateOrder,
  useRetryOrder,
  vendorsQueryOptions,
} from "@/lib/queries"
import {
  createOrderErrorMessage,
  createOrderTimeoutErrorMessage,
  isRecoverableOrderCreateError,
  readCreateOrderUnavailable,
} from "@/features/marketplace/lib/order-errors"
import { pollSentScriptsConfirmation } from "@/features/marketplace/lib/poll-sent-scripts-confirmation"
import {
  useIamPermission,
  useIsActiveSuperTenant,
  usePermissions,
} from "@/features/auth/hooks/usePermissions"
import { buildInitialOpportunitiesLinkSearch } from "@/features/opportunities/constants/opportunity-filters"
import { connectionNeedsResign } from "@/features/marketplace/lib/terms-compliance"
import { ConnectWizard } from "@/features/marketplace/components/ConnectWizard/ConnectWizard"
import { ReSignSheet } from "@/features/marketplace/components/ReSignSheet"
import { useConnectWizardStore } from "@/features/marketplace/store/connectWizard.store"
import { usePendingSendStore } from "@/features/marketplace/store/pendingSend.store"
import type {
  MarketplaceOrderStatus,
  SentScriptsMap,
  TenantVendorConnection,
  Vendor,
} from "@/features/marketplace/types/marketplace.types"
import { SendSuccessView } from "./SendSuccessView"
import { SendAlreadySentView } from "./SendAlreadySentView"
import { SendPendingRetryView } from "./SendPendingRetryView"
import {
  emptyHandoffDefaultsFormState,
  handoffDefaultsFromApi,
} from "@/features/marketplace/components/MarketplaceOrderHandoffDefaultsForm"
import { SendOrderHandoffStep } from "@/features/marketplace/components/SendOrderHandoffStep"
import { TargetRatioOrderSummary } from "@/features/marketplace/components/TargetRatiosPicker"
import { useTargetRatioSelection } from "@/features/marketplace/components/use-target-ratio-selection"
import { SendVendorCloseConfirm } from "@/features/marketplace/components/SendVendorCloseConfirm"
import {
  parseReviewRecipients,
  SendOrderReviewStep,
} from "@/features/marketplace/components/SendOrderReviewStep"
import { isOrderScriptSent, type MarketplaceOrderHandoffDefaults } from "@nexus/contracts"
import {
  SendVendorOptionCard,
  SendVendorOptionCardSkeleton,
  type SendVendorOptionStatus,
} from "./SendVendorOptionCard"

const SUCCESS_AUTO_CLOSE_MS = 5000

type SendDialogStep = "select-vendor" | "handoff-details" | "review"

interface SuccessInfo {
  vendorName: string
  count: number
  orderId?: string
}

interface PendingRetryInfo {
  vendorName: string
  orderId: string
  status: MarketplaceOrderStatus
}

interface VendorOption {
  vendor: Vendor
  connection: TenantVendorConnection | null
  status: SendVendorOptionStatus
}

export interface SendToVendorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All script IDs the user intends to send (may include already-sent). */
  scriptIds: string[]
  sentScriptsMap: SentScriptsMap
  tenantId: string
  opportunitySetId: string
  /** Ad platforms on the opportunity set. `google` is YouTube. */
  adPlatforms?: readonly string[] | string | null
}

function resolveVendorStatus(
  vendor: Vendor,
  connection: TenantVendorConnection | null,
): SendVendorOptionStatus {
  if (!connection || connection.disconnectedAt !== null) return "not-connected"
  if (!vendor.isActive) return "inactive"
  if (connectionNeedsResign(connection)) return "needs-resign"
  if (vendor.canReceiveOrders === false) return "no-contact-email"
  return "connected"
}

function vendorFromConnection(connection: TenantVendorConnection): Vendor {
  return {
    id: connection.vendor.id,
    name: connection.vendor.name,
    slug: connection.vendor.slug,
    description: connection.vendor.description,
    logoUrl: connection.vendor.logoUrl,
    websiteUrl: connection.vendor.websiteUrl,
    termsMarkdown: null,
    termsVersion: connection.vendor.termsVersion,
    isActive: connection.vendor.isActive,
    createdAt: connection.vendor.createdAt,
    updatedAt: connection.vendor.updatedAt,
    packages: [],
    questions: [],
  }
}

function mergeCatalogAndConnectionVendors(
  vendors: Vendor[],
  connections: TenantVendorConnection[],
): Vendor[] {
  const vendorById = new Map(vendors.map((vendor) => [vendor.id, vendor]))
  for (const connection of connections) {
    if (connection.disconnectedAt !== null) continue
    if (!vendorById.has(connection.vendorId)) {
      vendorById.set(connection.vendorId, vendorFromConnection(connection))
    }
  }
  return Array.from(vendorById.values())
}

function buildVendorOptions(
  vendors: Vendor[],
  connections: TenantVendorConnection[],
): VendorOption[] {
  const mergedVendors = mergeCatalogAndConnectionVendors(vendors, connections)
  const connectionByVendorId = new Map(
    connections
      .filter((c) => c.disconnectedAt === null)
      .map((c) => [c.vendorId, c] as const),
  )

  // Keep inactive vendors in the list so resolveVendorStatus can mark them
  // "inactive" (the card renders a disabled badge) and a pending intent that
  // targets a now-inactive vendor isn't silently dropped.
  return mergedVendors
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((vendor) => {
    const connection = connectionByVendorId.get(vendor.id) ?? null
    return {
      vendor,
      connection,
      status: resolveVendorStatus(vendor, connection),
    }
  })
}

export function SendToVendorDialog({
  open,
  onOpenChange,
  scriptIds,
  sentScriptsMap,
  tenantId,
  opportunitySetId,
  adPlatforms = null,
}: SendToVendorDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canReviewHandoffEmail = useIsActiveSuperTenant()
  const permissions = usePermissions()
  const canViewMarketplace = useIamPermission(permissions.MARKETPLACE_VIEW_PAGE)
  const [selectedVendorId, setSelectedVendorId] = React.useState("")
  const [resignOpen, setResignOpen] = React.useState(false)
  const [connectOpen, setConnectOpen] = React.useState(false)
  const [successInfo, setSuccessInfo] = React.useState<SuccessInfo | null>(null)
  const [pendingRetry, setPendingRetry] = React.useState<PendingRetryInfo | null>(null)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const [handoffOverrideNote, setHandoffOverrideNote] = React.useState("")
  const [handoffDefaults, setHandoffDefaults] = React.useState<MarketplaceOrderHandoffDefaults>(
    emptyHandoffDefaultsFormState(),
  )
  const [handoffDefaultsBaseline, setHandoffDefaultsBaseline] =
    React.useState<MarketplaceOrderHandoffDefaults>(emptyHandoffDefaultsFormState())
  const [recipientsTo, setRecipientsTo] = React.useState("")
  const [recipientsCc, setRecipientsCc] = React.useState("")
  const [handoffSubject, setHandoffSubject] = React.useState("")
  const [reviewCanSend, setReviewCanSend] = React.useState(true)
  const [step, setStep] = React.useState<SendDialogStep>("select-vendor")
  const [handoffConnectionId, setHandoffConnectionId] = React.useState("")
  const [isAwaitingSend, setIsAwaitingSend] = React.useState(false)
  const [closeConfirmOpen, setCloseConfirmOpen] = React.useState(false)
  const successHandledRef = React.useRef(false)
  const pollAbortRef = React.useRef<AbortController | null>(null)
  const nestedDialogOpenRef = React.useRef(false)
  // Guards the connection-id → vendor-id hydration so a connections refetch
  // can't clobber a selection the user (or the open-reset) already made.
  const pendingHydratedRef = React.useRef(false)

  React.useEffect(() => {
    return () => {
      pollAbortRef.current?.abort()
    }
  }, [])

  const pendingIntent = usePendingSendStore((s) => s.intent)
  const setPendingIntent = usePendingSendStore((s) => s.setIntent)
  const clearPendingIntent = usePendingSendStore((s) => s.clearIntent)
  const openWizard = useConnectWizardStore((s) => s.openWizard)
  const closeWizard = useConnectWizardStore((s) => s.closeWizard)

  const vendorsQuery = useQuery(vendorsQueryOptions(tenantId))
  const connectionsQuery = useQuery(connectionsQueryOptions(tenantId))
  const connections = connectionsQuery.data ?? []
  const vendorOptions = buildVendorOptions(vendorsQuery.data ?? [], connections)

  const createOrder = useCreateOrder()
  const retryOrder = useRetryOrder()
  const queryClient = useQueryClient()
  const isSubmitting = createOrder.isPending || isAwaitingSend || retryOrder.isPending

  const ratioSelection = useTargetRatioSelection({
    open,
    adPlatforms,
    platformsReady: true,
  })
  const alreadySentIds = scriptIds.filter((id) => isOrderScriptSent(sentScriptsMap[id]))
  const unsentIds = scriptIds.filter((id) => !isOrderScriptSent(sentScriptsMap[id]))
  const allAlreadySent = scriptIds.length > 0 && unsentIds.length === 0

  const selectedOption = vendorOptions.find((o) => o.vendor.id === selectedVendorId)
  const selectedConnection = selectedOption?.connection ?? null
  const selectedNeedsResign = selectedOption?.status === "needs-resign"
  const selectedNotConnected = selectedOption?.status === "not-connected"
  const selectedNoContactEmail = selectedOption?.status === "no-contact-email"
  const selectedCanSend = selectedOption?.status === "connected"
  const sendsOnPrimaryAction =
    step === "review" || (!canReviewHandoffEmail && step === "handoff-details")

  const handoffDefaultsQuery = useQuery(
    orderHandoffDefaultsQueryOptions(
      step === "handoff-details" ? tenantId : null,
      step === "handoff-details" ? handoffConnectionId : null,
    ),
  )

  const isLoading = vendorsQuery.isLoading || connectionsQuery.isLoading
  const loadFailed = vendorsQuery.isError || connectionsQuery.isError

  const wasOpenRef = React.useRef(false)

  React.useEffect(() => {
    const opening = open && !wasOpenRef.current
    wasOpenRef.current = open

    if (!open) return

    if (!opening) return

    setSuccessInfo(null)
    setPendingRetry(null)
    setSendError(null)
    setHandoffOverrideNote("")
    setHandoffDefaults(emptyHandoffDefaultsFormState())
    setHandoffDefaultsBaseline(emptyHandoffDefaultsFormState())
    setRecipientsTo("")
    setRecipientsCc("")
    setHandoffSubject("")
    setReviewCanSend(true)
    setStep("select-vendor")
    setHandoffConnectionId("")
    setIsAwaitingSend(false)
    pollAbortRef.current?.abort()
    successHandledRef.current = false
    pendingHydratedRef.current = false
    createOrder.reset()

    const sameTenant = pendingIntent?.tenantId === tenantId
    if (sameTenant && pendingIntent?.vendorId && !pendingIntent.tenantVendorId) {
      setSelectedVendorId(pendingIntent.vendorId)
      pendingHydratedRef.current = true
    } else if (!(sameTenant && pendingIntent?.tenantVendorId)) {
      // No connection-id to resolve — selection is final here.
      setSelectedVendorId("")
      pendingHydratedRef.current = true
    }
    // The tenantVendorId case needs `connections`, which may not have loaded
    // yet; the hydration effect below resolves it once they arrive.
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, pendingIntent?.vendorId]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open || pendingHydratedRef.current) return
    if (pendingIntent?.tenantId !== tenantId || !pendingIntent.tenantVendorId) return
    const conn = connections.find((c) => c.id === pendingIntent.tenantVendorId)
    if (conn) {
      setSelectedVendorId(conn.vendorId)
      pendingHydratedRef.current = true
    }
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, connections])

  React.useEffect(() => {
    if (
      selectedVendorId &&
      vendorOptions.length > 0 &&
      !vendorOptions.some((o) => o.vendor.id === selectedVendorId)
    ) {
      setSelectedVendorId("")
    }
  }, [selectedVendorId, vendorOptions])

  React.useEffect(() => {
    if (handoffDefaultsQuery.data) {
      const preview = handoffDefaultsQuery.data
      const defaults = "defaults" in preview ? preview.defaults : preview
      const recipients = "recipients" in preview ? preview.recipients : { to: [], cc: [] }
      const parsed = handoffDefaultsFromApi(defaults)
      setHandoffDefaults(parsed)
      setHandoffDefaultsBaseline(parsed)
      setRecipientsTo(recipients.to.join(", "))
      setRecipientsCc(recipients.cc.join(", "))
    }
  }, [handoffDefaultsQuery.data])

  function submitOrder(connectionId: string, ids: string[]) {
    setSendError(null)
    pollAbortRef.current?.abort()
    const pollAbort = new AbortController()
    pollAbortRef.current = pollAbort

    createOrder.mutate(
      {
        tenantId,
        tenantVendorId: connectionId,
        scriptIds: ids,
        handoffDefaultsOverride: handoffDefaults,
        targetRatios: ratioSelection.selected,
        ...(handoffOverrideNote.trim()
          ? { handoffOverrideNote: handoffOverrideNote.trim() }
          : {}),
        ...(canReviewHandoffEmail
          ? {
              recipientEmails: parseReviewRecipients(recipientsTo),
              ccEmails: parseReviewRecipients(recipientsCc),
              handoffSubject: handoffSubject.trim() || undefined,
            }
          : {}),
      },
      {
        onSuccess: (result) => {
          const conn = connections.find((c) => c.id === connectionId)
          const vendorName = conn?.vendor.name ?? t("opportunities.scripts.sendDialog.vendorLabel")
          successHandledRef.current = false
          setSuccessInfo({
            vendorName,
            count: ids.length,
            orderId: result.orderId ?? result.data?.id ?? undefined,
          })
        },
        onError: async (err) => {
          if (isRecoverableOrderCreateError(err)) {
            const conn = connections.find((c) => c.id === connectionId)
            const vendorName =
              conn?.vendor.name ?? t("opportunities.scripts.sendDialog.vendorLabel")
            const seeded = readCreateOrderUnavailable(err)
            if (seeded) {
              setPendingRetry({
                vendorName,
                orderId: seeded.orderId,
                status: seeded.status,
              })
            }
            setIsAwaitingSend(true)
            const polled = await pollSentScriptsConfirmation(
              queryClient,
              tenantId,
              ids,
              pollAbort.signal,
            )
            if (pollAbort.signal.aborted) return
            setIsAwaitingSend(false)
            if (polled.outcome === "sent") {
              successHandledRef.current = false
              setSuccessInfo({ vendorName, count: ids.length, orderId: polled.orderId })
              setPendingRetry(null)
              return
            }
            if (polled.outcome === "pending") {
              setPendingRetry({
                vendorName,
                orderId: polled.orderId,
                status: polled.status,
              })
              return
            }
            if (seeded) return
            setSendError(createOrderTimeoutErrorMessage(t))
            return
          }
          setSendError(createOrderErrorMessage(err, t))
        },
      },
    )
  }

  function handleSuccessDone() {
    if (successHandledRef.current || !successInfo) {
      onOpenChange(false)
      return
    }
    successHandledRef.current = true
    toast.success(
      successInfo.count === 1
        ? t("opportunities.scripts.sendDialog.successToast_one", { vendorName: successInfo.vendorName })
        : t("opportunities.scripts.sendDialog.successToast_other", {
            count: successInfo.count,
            vendorName: successInfo.vendorName,
          }),
    )
    clearPendingIntent()
    onOpenChange(false)
  }

  function viewOrderHref(orderId: string): string {
    if (canViewMarketplace) {
      return `/marketplace?tab=in-production&order=${encodeURIComponent(orderId)}`
    }
    const search = new URLSearchParams({
      setId: opportunitySetId,
      tab: "orders",
      orderId,
    })
    return `/opportunities?${search.toString()}`
  }

  function navigateToOrder(orderId: string) {
    if (canViewMarketplace) {
      void navigate({
        to: "/marketplace",
        search: { tab: "in-production", order: orderId },
      })
      return
    }
    void navigate({
      to: "/opportunities",
      search: buildInitialOpportunitiesLinkSearch({
        setId: opportunitySetId,
        tab: "orders",
        orderId,
      }),
    })
  }

  function handleSend() {
    if (!selectedOption || unsentIds.length === 0) return

    if (
      (step === "review" || step === "handoff-details") &&
      ratioSelection.selected.length === 0
    ) {
      return
    }

    if (step === "review") {
      if (!handoffConnectionId) return
      submitOrder(handoffConnectionId, unsentIds)
      return
    }

    if (step === "handoff-details") {
      if (!handoffConnectionId) return
      if (canReviewHandoffEmail) {
        setStep("review")
        return
      }
      submitOrder(handoffConnectionId, unsentIds)
      return
    }

    if (selectedNotConnected) {
      nestedDialogOpenRef.current = true
      setPendingIntent({ tenantId, scriptIds: unsentIds, vendorId: selectedOption.vendor.id })
      openWizard(selectedOption.vendor.id)
      setConnectOpen(true)
      return
    }

    if (!selectedConnection) return

    if (selectedNeedsResign) {
      nestedDialogOpenRef.current = true
      setPendingIntent({
        tenantId,
        tenantVendorId: selectedConnection.id,
        scriptIds: unsentIds,
      })
      setResignOpen(true)
      return
    }

    if (selectedCanSend && selectedConnection) {
      setHandoffConnectionId(selectedConnection.id)
      setStep("handoff-details")
    }
  }

  function handleConnectSuccess(connection: TenantVendorConnection) {
    nestedDialogOpenRef.current = false
    setConnectOpen(false)
    closeWizard()
    clearPendingIntent()
    setSelectedVendorId(connection.vendorId)
    setHandoffConnectionId(connection.id)
    setStep("handoff-details")
  }

  function handleConnectClose() {
    nestedDialogOpenRef.current = false
    setConnectOpen(false)
    closeWizard()
  }

  function handleResignSuccess() {
    const intent = usePendingSendStore.getState().intent
    if (!intent?.tenantVendorId || intent.tenantId !== tenantId) return
    nestedDialogOpenRef.current = false
    setResignOpen(false)
    setHandoffConnectionId(intent.tenantVendorId)
    setStep("handoff-details")
  }

  function handleResignClose() {
    nestedDialogOpenRef.current = false
    setResignOpen(false)
  }

  function handleDialogClose(nextOpen: boolean) {
    if (nextOpen) return
    if (successInfo) {
      handleSuccessDone()
      return
    }
    if (pendingRetry) {
      onOpenChange(false)
      return
    }
    if (createOrder.isPending || isAwaitingSend) return
    if (!resignOpen && !connectOpen) clearPendingIntent()
    onOpenChange(false)
  }

  function handleRetryLoad() {
    void vendorsQuery.refetch()
    void connectionsQuery.refetch()
  }

  React.useEffect(() => {
    if (!open) {
      nestedDialogOpenRef.current = false
      setResignOpen(false)
      setConnectOpen(false)
      setCloseConfirmOpen(false)
    }
  }, [open])

  const dialogHidden = resignOpen || connectOpen
  const isResultView = Boolean(successInfo || pendingRetry || allAlreadySent)

  return (
    <>
      <Dialog open={open && !dialogHidden} onOpenChange={handleDialogClose}>
        <DialogContent
          variant={isResultView ? "confirmation" : "form"}
          className={cn(
            "flex w-full flex-col overflow-hidden",
            !isResultView &&
              step === "review" &&
              "sm:max-w-[min(1100px,calc(100vw-2rem))]",
            !isResultView && step === "review"
              ? "h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)]"
              : "max-h-[min(92vh,960px)]",
          )}
          onInteractOutside={(event) => {
            event.preventDefault()
            if (nestedDialogOpenRef.current || successInfo || pendingRetry || isSubmitting) return
            setCloseConfirmOpen(true)
          }}
        >
          {pendingRetry ? (
            <SendPendingRetryView
              vendorName={pendingRetry.vendorName}
              status={pendingRetry.status}
              retrying={retryOrder.isPending}
              onRetry={() => {
                pollAbortRef.current?.abort()
                const pollAbort = new AbortController()
                pollAbortRef.current = pollAbort
                retryOrder.mutate(
                  { orderId: pendingRetry.orderId, tenantId },
                  {
                    onSuccess: async () => {
                      setIsAwaitingSend(true)
                      const polled = await pollSentScriptsConfirmation(
                        queryClient,
                        tenantId,
                        unsentIds.length > 0 ? unsentIds : scriptIds,
                        pollAbort.signal,
                      )
                      if (pollAbort.signal.aborted) return
                      setIsAwaitingSend(false)
                      if (polled.outcome === "sent") {
                        successHandledRef.current = false
                        setPendingRetry(null)
                        setSuccessInfo({
                          vendorName: pendingRetry.vendorName,
                          count: unsentIds.length || scriptIds.length,
                          orderId: polled.orderId,
                        })
                        return
                      }
                      if (polled.outcome === "pending") {
                        setPendingRetry({
                          ...pendingRetry,
                          orderId: polled.orderId,
                          status: polled.status,
                        })
                      }
                    },
                  },
                )
              }}
              onViewOrder={() => {
                navigateToOrder(pendingRetry.orderId)
                onOpenChange(false)
              }}
              onClose={() => onOpenChange(false)}
            />
          ) : successInfo ? (
            <SendSuccessView
              vendorName={successInfo.vendorName}
              count={successInfo.count}
              autoCloseMs={SUCCESS_AUTO_CLOSE_MS}
              onDone={handleSuccessDone}
              orderId={successInfo.orderId}
              viewOrderHref={
                successInfo.orderId ? viewOrderHref(successInfo.orderId) : undefined
              }
              onViewOrder={() => {
                if (!successInfo.orderId) return
                navigateToOrder(successInfo.orderId)
              }}
              onSendAnother={() => {
                successHandledRef.current = true
                setSuccessInfo(null)
                setHandoffSubject("")
                setReviewCanSend(true)
                setStep("select-vendor")
                setHandoffConnectionId("")
              }}
            />
          ) : allAlreadySent ? (
            <SendAlreadySentView
              scriptIds={scriptIds}
              sentScriptsMap={sentScriptsMap}
              onClose={() => onOpenChange(false)}
            />
          ) : (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="text-base">
                  {t("opportunities.scripts.sendDialog.title", "Send to production partner")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    `opportunities.scripts.sendDialog.description_${unsentIds.length === 1 ? "one" : "other"}`,
                    { count: unsentIds.length },
                  )}
                </DialogDescription>
              </DialogHeader>

              <div
                className={cn(
                  "min-h-0 flex-1 px-1 -mx-1",
                  step === "review" ? "flex flex-col overflow-hidden" : "overflow-y-auto",
                )}
              >
              {loadFailed ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="alert-circle" size="sm" className="mx-auto mb-2 text-destructive/60" />
                  <p className="text-sm font-medium text-foreground">
                    {vendorsQuery.isError
                      ? t("opportunities.scripts.sendDialog.vendorsFailed")
                      : t("opportunities.scripts.sendDialog.connectionsFailed")}
                  </p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={handleRetryLoad}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : isLoading ? (
                <div className="flex flex-col gap-2">
                  <SendVendorOptionCardSkeleton />
                  <SendVendorOptionCardSkeleton />
                </div>
              ) : vendorOptions.length === 0 ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="building-2" size="sm" className="mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-sm font-medium text-foreground">
                    {t("opportunities.scripts.sendDialog.noVendors")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("opportunities.scripts.sendDialog.noVendorsHint")}
                  </p>
                </div>
              ) : (
                <div className={cn("flex flex-col gap-3", step === "review" && "min-h-0 flex-1 overflow-hidden")}>
                  {sendError && !isSubmitting && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-destructive" />
                      <p className="text-xs text-foreground/80">{sendError}</p>
                    </div>
                  )}

                  {isSubmitting && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
                    >
                      <Icon
                        name="loader"
                        size="xs"
                        className="mt-0.5 shrink-0 animate-spin text-muted-foreground"
                      />
                      <p className="text-xs text-foreground/80">
                        {t("opportunities.scripts.sendDialog.confirming")}
                      </p>
                    </div>
                  )}

                  {alreadySentIds.length > 0 && !allAlreadySent && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t(
                          alreadySentIds.length === 1
                            ? "opportunities.scripts.sendDialog.alreadySentWarning_one"
                            : "opportunities.scripts.sendDialog.alreadySentWarning_other",
                          { count: alreadySentIds.length },
                        )}
                      </p>
                    </div>
                  )}

                  {selectedNeedsResign && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t("marketplace.resign.sendBlocked")}
                      </p>
                    </div>
                  )}

                  {selectedNoContactEmail && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t("opportunities.scripts.sendDialog.noContactEmailBlocked")}
                      </p>
                    </div>
                  )}

                  {!allAlreadySent && step === "handoff-details" ? (
                    <SendOrderHandoffStep
                      handoffOverrideNote={handoffOverrideNote}
                      onHandoffOverrideNoteChange={setHandoffOverrideNote}
                      handoffDefaults={handoffDefaults}
                      onHandoffDefaultsChange={setHandoffDefaults}
                      handoffDefaultsBaseline={handoffDefaultsBaseline}
                      platformFamily={ratioSelection.family}
                      targetRatios={ratioSelection.selected}
                      onToggleTargetRatio={ratioSelection.toggle}
                      ratiosReady={ratioSelection.isReady}
                      showEmptyError={ratioSelection.showEmptyError}
                      disabled={isSubmitting}
                      isLoadingDefaults={handoffDefaultsQuery.isLoading}
                      defaultsFailed={handoffDefaultsQuery.isError}
                      onRetryDefaults={() => void handoffDefaultsQuery.refetch()}
                    />
                  ) : null}

                  {!allAlreadySent && step === "review" ? (
                    <SendOrderReviewStep
                      tenantId={tenantId}
                      tenantVendorId={handoffConnectionId || selectedOption?.connection?.id || ""}
                      scriptIds={unsentIds}
                      vendorName={selectedOption?.vendor.name ?? ""}
                      handoffOverrideNote={handoffOverrideNote}
                      handoffDefaults={handoffDefaults}
                      targetRatios={ratioSelection.selected}
                      baseline={handoffDefaultsBaseline}
                      recipientsTo={recipientsTo}
                      recipientsCc={recipientsCc}
                      subject={handoffSubject}
                      onRecipientsToChange={setRecipientsTo}
                      onRecipientsCcChange={setRecipientsCc}
                      onSubjectChange={setHandoffSubject}
                      onCanSendChange={setReviewCanSend}
                      onEditSource={() => setStep("handoff-details")}
                      disabled={isSubmitting}
                    />
                  ) : null}

                  {!allAlreadySent && step === "select-vendor" && (
                    <div
                      className="flex flex-col gap-2"
                      role="listbox"
                      aria-label={t("opportunities.scripts.sendDialog.vendorLabel")}
                    >
                      {vendorOptions.map((option) => (
                        <SendVendorOptionCard
                          key={option.vendor.id}
                          vendorName={option.vendor.name}
                          logoUrl={option.connection?.vendor.logoUrl ?? option.vendor.logoUrl}
                          description={option.vendor.description}
                          selected={selectedVendorId === option.vendor.id}
                          status={option.status}
                          onSelect={() => setSelectedVendorId(option.vendor.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>

              <DialogFooter className="shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {!allAlreadySent && (step === "handoff-details" || step === "review") ? (
                  <TargetRatioOrderSummary
                    scriptCount={unsentIds.length}
                    ratioCount={ratioSelection.selected.length}
                  />
                ) : (
                  <span className="hidden sm:block" />
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {loadFailed || vendorOptions.length === 0 ? (
                  <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenChange(false)}
                      disabled={isSubmitting}
                    >
                      {t("common.cancel")}
                    </Button>
                    {!allAlreadySent && (step === "handoff-details" || step === "review") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setStep(step === "review" ? "handoff-details" : "select-vendor")
                        }
                        disabled={isSubmitting}
                      >
                        {t("common.back")}
                      </Button>
                    )}
                    {!allAlreadySent && selectedVendorId && (
                      <Button
                        size="sm"
                        onClick={handleSend}
                        disabled={
                          isSubmitting ||
                          (step === "handoff-details" &&
                            (handoffDefaultsQuery.isLoading ||
                              handoffDefaultsQuery.isError ||
                              !ratioSelection.isReady ||
                              ratioSelection.selected.length === 0)) ||
                          (step === "review" && ratioSelection.selected.length === 0) ||
                          (step === "review" &&
                            (parseReviewRecipients(recipientsTo).length === 0 || !reviewCanSend)) ||
                          (step === "select-vendor" &&
                            (selectedOption?.status === "inactive" ||
                              selectedNoContactEmail ||
                              (!selectedCanSend && !selectedNotConnected && !selectedNeedsResign)))
                        }
                        iconStart={
                          isSubmitting
                            ? "loader"
                            : sendsOnPrimaryAction
                              ? "upload"
                              : selectedNeedsResign
                                ? "file-text"
                                : selectedNotConnected
                                  ? "link"
                                  : "arrow-right"
                        }
                        className={isSubmitting ? "[&_svg]:animate-spin" : ""}
                      >
                        {isSubmitting
                          ? t("opportunities.scripts.sendDialog.confirming")
                          : sendsOnPrimaryAction
                            ? t("marketplace.sendHandoff.sendToVendor", {
                                vendor: selectedOption?.vendor.name ?? "",
                                defaultValue: "Send to {{vendor}}",
                              })
                            : step === "handoff-details"
                              ? t("marketplace.sendHandoff.reviewContinue", "Review email")
                            : selectedNeedsResign
                              ? t("marketplace.resign.action")
                              : selectedNotConnected
                                ? t("opportunities.scripts.sendDialog.connectAndSend")
                                : t("opportunities.scripts.sendDialog.continue")}
                      </Button>
                    )}
                  </>
                )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SendVendorCloseConfirm
        open={closeConfirmOpen}
        onStay={() => setCloseConfirmOpen(false)}
        onClose={() => {
          setCloseConfirmOpen(false)
          handleDialogClose(false)
        }}
      />

      {selectedOption && connectOpen && (
        <ConnectWizard
          vendorId={selectedOption.vendor.id}
          tenantId={tenantId}
          open={connectOpen}
          onClose={handleConnectClose}
          onConnectSuccess={handleConnectSuccess}
        />
      )}

      {selectedConnection && (
        <ReSignSheet
          connectionId={selectedConnection.id}
          vendorId={selectedConnection.vendorId}
          tenantId={tenantId}
          open={resignOpen}
          onClose={handleResignClose}
          onSuccess={handleResignSuccess}
        />
      )}
    </>
  )
}
